import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["photoUrl", "referralProgram", "userEmail", "userPhone", "isInitiallyOpen", "liveStreamingState", "personalProductListConfiguration", "videoShoppingConfiguration"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import useTestId from '@ori/testid-generator';
import { ArrowDownThin, ArrowUpThin, Pbs as PbsWaveIcon } from '@ori-ui/icons';
import { Button, Link, Stack, styled, Typography } from '@ori-ui/mui';
import { useCallback, useState } from 'react';
import { LinkWrap, StyledAvatar, MenuItemPersonalLinks } from "./MenuItemPersonalLinks";
import { LiveStreamingStateWrap } from "../common/LiveStreamingStateWrap";
import { Trans } from "../Trans";
import { useTranslation } from "../../hooks";
import { useIsMobile } from "../../hooks/useIsMobile";
import { LiveStreamingStates, MenuItemTestIds, TranslationKeys } from "../../models";

/**
 * How large fallback icon representing avatar should be
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var fallBackAvatarIconSize = 20;
var Root = styled('div')(function (_ref) {
  var theme = _ref.theme,
    onClick = _ref.onClick;
  return {
    backgroundColor: theme.palette.common.white,
    width: '100%'
  };
});
var ItemTitleWrap = styled(Stack)(function (_ref2) {
  var theme = _ref2.theme;
  return {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5, 6),
    alignItems: 'center',
    position: 'relative',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  };
});
var Title = styled(Typography)(function (_ref3) {
  var theme = _ref3.theme;
  return {
    display: 'inline-block',
    fontWeight: 'bold',
    marginLeft: theme.spacing(1.5)
  };
});
var StyledButton = styled(Button)(function (_ref4) {
  var theme = _ref4.theme;
  return {
    padding: 5,
    minWidth: 32,
    fontSize: theme.typography.pxToRem(24)
  };
});
var ItemContentWrap = styled(Stack)(function (_ref5) {
  var open = _ref5.open,
    theme = _ref5.theme;
  return {
    display: open ? 'block' : 'none',
    padding: theme.spacing(6),
    paddingTop: 0
  };
});
var RightContentWrap = styled('div')({
  position: 'absolute',
  right: 8
});
var StyledBreak = styled('hr')(function (_ref6) {
  var theme = _ref6.theme;
  return {
    backgroundColor: theme.palette.grey[100],
    border: 'none',
    height: 2,
    margin: 0,
    marginBottom: theme.spacing(1.5)
  };
});
export var MenuItem = function MenuItem(_ref7) {
  var photoUrl = _ref7.photoUrl,
    referralProgram = _ref7.referralProgram,
    userEmail = _ref7.userEmail,
    userPhone = _ref7.userPhone,
    isInitiallyOpen = _ref7.isInitiallyOpen,
    liveStreamingState = _ref7.liveStreamingState,
    personalProductListConfiguration = _ref7.personalProductListConfiguration,
    videoShoppingConfiguration = _ref7.videoShoppingConfiguration,
    restProps = _objectWithoutProperties(_ref7, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useTestId = useTestId(),
    getTestIdProps = _useTestId.getTestIdProps;
  var _useState = useState(isInitiallyOpen),
    _useState2 = _slicedToArray(_useState, 2),
    isItemOpen = _useState2[0],
    setIsItemOpen = _useState2[1];
  var isMobile = useIsMobile();
  var handleItemClick = useCallback(function () {
    return isMobile ? setIsItemOpen(function (isItemCurrentlyOpen) {
      return !isItemCurrentlyOpen;
    }) : undefined;
  }, [isMobile]);
  var isOpen = isMobile && isItemOpen;
  var _ref8 = referralProgram !== null && referralProgram !== void 0 ? referralProgram : {},
    benefitsLink = _ref8.benefitsLink,
    message = _ref8.message,
    title = _ref8.title;
  return /*#__PURE__*/_jsxs(Root, _objectSpread(_objectSpread(_objectSpread({}, getTestIdProps(MenuItemTestIds.ROOT)), restProps), {}, {
    children: [/*#__PURE__*/_jsxs(ItemTitleWrap, _objectSpread(_objectSpread({
      direction: "row",
      onClick: handleItemClick
    }, getTestIdProps(MenuItemTestIds.TITLE_WRAP)), {}, {
      children: [/*#__PURE__*/_jsx(LiveStreamingStateWrap, {
        streamingActivity: liveStreamingState !== null && liveStreamingState !== void 0 ? liveStreamingState : LiveStreamingStates.UNKNOWN,
        children: /*#__PURE__*/_jsx(StyledAvatar, _objectSpread(_objectSpread({
          src: photoUrl !== null && photoUrl !== void 0 ? photoUrl : '',
          alt: t(TranslationKeys.SponsorPhoto)
        }, getTestIdProps(MenuItemTestIds.AVATAR)), {}, {
          children: /*#__PURE__*/_jsx(PbsWaveIcon, _objectSpread({
            sx: {
              width: fallBackAvatarIconSize,
              height: fallBackAvatarIconSize
            },
            viewBox: "2.8 4 16.5 16"
          }, getTestIdProps(MenuItemTestIds.FALLBACK_AVATAR)))
        }))
      }), /*#__PURE__*/_jsx(Title, _objectSpread(_objectSpread({
        variant: "body2",
        color: "textPrimary"
      }, getTestIdProps(MenuItemTestIds.TITLE)), {}, {
        children: title
      })), isMobile ? /*#__PURE__*/_jsx(RightContentWrap, {
        children: /*#__PURE__*/_jsx(StyledButton, _objectSpread(_objectSpread({
          variant: "text",
          color: "default",
          size: "small"
        }, getTestIdProps(MenuItemTestIds.EXPANDER)), {}, {
          children: isItemOpen ? /*#__PURE__*/_jsx(ArrowUpThin, {
            fontSize: "inherit"
          }) : /*#__PURE__*/_jsx(ArrowDownThin, {
            fontSize: "inherit"
          })
        }))
      }) : undefined]
    })), isOpen ? /*#__PURE__*/_jsxs(ItemContentWrap, {
      open: isOpen,
      direction: "column",
      spacing: 2,
      children: [/*#__PURE__*/_jsx(Typography, _objectSpread(_objectSpread({
        variant: "body2",
        color: "textPrimary"
      }, getTestIdProps(MenuItemTestIds.TEXT)), {}, {
        children: /*#__PURE__*/_jsx(Trans, {
          defaults: message !== null && message !== void 0 ? message : undefined,
          components: {
            emphasis: /*#__PURE__*/_jsx("strong", _objectSpread({}, getTestIdProps(MenuItemTestIds.USER_NAME)))
          }
        })
      })), benefitsLink !== null && benefitsLink !== void 0 && benefitsLink.relativeUrl && benefitsLink.title ? /*#__PURE__*/_jsx(LinkWrap, {
        children: /*#__PURE__*/_jsx(Link, _objectSpread(_objectSpread({
          href: benefitsLink.relativeUrl,
          variant: "button"
        }, getTestIdProps(MenuItemTestIds.BENEFITS_LINK)), {}, {
          children: benefitsLink.title
        }))
      }) : null, userEmail ? /*#__PURE__*/_jsx(LinkWrap, {
        children: /*#__PURE__*/_jsx(Link, _objectSpread(_objectSpread({
          href: "mailto:".concat(userEmail)
        }, getTestIdProps(MenuItemTestIds.USER_EMAIL)), {}, {
          children: userEmail
        }))
      }) : null, userPhone ? /*#__PURE__*/_jsx(LinkWrap, {
        children: /*#__PURE__*/_jsx(Link, _objectSpread(_objectSpread({
          href: "tel:".concat(userPhone)
        }, getTestIdProps(MenuItemTestIds.USER_PHONE)), {}, {
          children: userPhone
        }))
      }) : null, /*#__PURE__*/_jsx(MenuItemPersonalLinks, {
        personalProductListConfiguration: personalProductListConfiguration,
        videoShoppingConfiguration: videoShoppingConfiguration,
        liveStreamingState: liveStreamingState,
        photoUrl: photoUrl
      })]
    }) : undefined, isMobile ? /*#__PURE__*/_jsx(StyledBreak, {}) : null]
  }));
};